import type { KnowledgeKind, KnowledgeListItem } from '@fos/shared/contracts/knowledge';
import { BookOpen } from 'lucide-react';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ListRow } from '../../../ui/ListRow';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { itemSubtitle, KNOWLEDGE_KIND_ICONS } from '../knowledge-format';

export function KnowledgeRow({
  item,
  showProject = true,
  active = false,
}: {
  item: KnowledgeListItem;
  showProject?: boolean;
  active?: boolean;
}) {
  const Icon = KNOWLEDGE_KIND_ICONS[item.kind];
  return (
    <ListRow
      to={`/wiedza/${item.id}`}
      leading={<Icon size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />}
      title={item.title}
      description={itemSubtitle(item, new Date(), { project: showProject })}
      className={active ? 'bg-elevated' : undefined}
    />
  );
}

/**
 * Wiedza klienta albo projektu (zakładka „Wiedza” w profilu klienta i projekcie, Figma 10): notatki, linki, pliki
 * (i decyzje, chyba że `excludeDecisions`), najnowsze zmiany najpierw; wiersz prowadzi do /wiedza/:id.
 * Zakres i moduł sprawdza serwer (`knowledge.list`); bez modułu Wiedza komponent nic nie pokazuje.
 */
export function KnowledgeList({
  clientId,
  projectId,
  kind,
  excludeDecisions = false,
  limit,
  emptyTitle = 'Brak notatek',
  emptyDescription = 'Notatki, linki i pliki pojawią się tutaj.',
}: {
  clientId?: string;
  projectId?: string;
  kind?: KnowledgeKind;
  excludeDecisions?: boolean;
  limit?: number;
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const access = useAccess();
  const enabled = access.can('knowledge');
  const query = trpc.knowledge.list.useQuery(
    {
      ...(clientId ? { clientId } : {}),
      ...(projectId ? { projectId } : {}),
      ...(kind ? { kind } : {}),
      ...(limit ? { limit } : {}),
    },
    { enabled },
  );
  if (!enabled) return null;
  if (query.isPending) return <ListSkeleton rows={3} />;
  if (query.isError) {
    return (
      <ErrorState
        compact
        title="Nie udało się wczytać wiedzy"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  const items = excludeDecisions ? query.data.filter((i) => i.kind !== 'decision') : query.data;
  if (items.length === 0) {
    return <EmptyState compact icon={BookOpen} title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <ul aria-label="Wiedza">
      {items.map((item) => (
        <li key={item.id}>
          <KnowledgeRow item={item} showProject={!projectId} />
        </li>
      ))}
    </ul>
  );
}
